import { useEffect, useState } from 'react';
import { Button, Card, Col, Descriptions, Input, Modal, Row, Select, Space, Statistic, Tabs, Tag, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import PageTitle from '../../components/PageTitle';
import ListToolbar from '../../components/ListToolbar';
import ResizableTable from '../../components/ResizableTable';
import { exportToCSV } from '../../utils/exportUtils';
import { getAdminVoiceNotifyOrders, getAdminVoiceNotifySubscriptions, getOrderDetail, setAdminVoiceNotifySubscriptionAdminDisabled } from '../../api';

const grantTag = (text, status) => {
  if (status === 1) return <Tag color="success">{text || '已开通'}</Tag>;
  if (status === 2) return <Tag color="error">{text || '开通失败'}</Tag>;
  return <Tag>{text || '待开通'}</Tag>;
};

const activeSubTag = (r) => {
  if (r?.admin_disabled) {
    return <Tag color="warning">{r.active_text || r.disabled_by_text || '已关闭'}</Tag>;
  }
  if (r?.active) {
    return <Tag color="success">{r.active_text || '有效'}</Tag>;
  }
  return <Tag>{r?.active_text || '已过期'}</Tag>;
};

const TIME_MODE_OPTIONS = [
  { value: 'month', label: '一月' },
  { value: 'day', label: '一天' },
];

const buildPayMonthOptions = () => {
  const opts = [{ value: '', label: '全部月份' }];
  const now = new Date();
  for (let i = 0; i < 36; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    opts.push({ value: `${y}${String(m).padStart(2, '0')}`, label: `${y}年${m}月` });
  }
  return opts;
};

const PAY_MONTH_OPTIONS = buildPayMonthOptions();

const OrderTab = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [timeMode, setTimeMode] = useState('month');
  const [payMonth, setPayMonth] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [summary, setSummary] = useState({ count: 0, total_amount: 0 });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [rechargeModal, setRechargeModal] = useState({ open: false, loading: false, detail: null });

  const fetchList = async (search, page, pageSize, month = payMonth, start = startDate, end = endDate) => {
    setLoading(true);
    try {
      const res = await getAdminVoiceNotifyOrders(search, page, pageSize, month, start, end);
      if (res?.success && Array.isArray(res.data)) {
        setData(res.data);
        setPagination((prev) => ({
          ...prev,
          current: res.page ?? page,
          pageSize: res.page_size ?? pageSize,
          total: Number(res.total) || 0,
        }));
        setSummary({
          count: Number(res.summary?.count ?? res.total) || 0,
          total_amount: Number(res.summary?.total_amount) || 0,
        });
      } else {
        setData([]);
        setSummary({ count: 0, total_amount: 0 });
        message.error(res?.error || '查询失败');
      }
    } catch (err) {
      setData([]);
      setSummary({ count: 0, total_amount: 0 });
      message.error(err.response?.data?.error || err.message || '查询失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList('', 1, 20, '', '', '');
  }, []);

  const handleSearch = () => {
    fetchList(searchText.trim(), 1, pagination.pageSize, payMonth, startDate, endDate);
  };

  const handleTableChange = (p) => {
    fetchList(searchText.trim(), p.current, p.pageSize, payMonth, startDate, endDate);
  };

  const handleTimeModeChange = (mode) => {
    setTimeMode(mode || 'month');
    setPayMonth('');
    setStartDate('');
    setEndDate('');
    fetchList(searchText.trim(), 1, pagination.pageSize, '', '', '');
  };

  const handlePayMonthChange = (value) => {
    const month = value || '';
    setPayMonth(month);
    setStartDate('');
    setEndDate('');
    fetchList(searchText.trim(), 1, pagination.pageSize, month, '', '');
  };

  const handleStartDateChange = (e) => {
    const start = e.target.value || '';
    setStartDate(start);
    setPayMonth('');
    fetchList(searchText.trim(), 1, pagination.pageSize, '', start, endDate);
  };

  const handleEndDateChange = (e) => {
    const end = e.target.value || '';
    setEndDate(end);
    setPayMonth('');
    fetchList(searchText.trim(), 1, pagination.pageSize, '', startDate, end);
  };

  const handleExport = () => {
    if (!data.length) {
      message.warning('当前页没有可导出数据');
      return;
    }
    const rows = data.map((r) => ({
      通知单号: r.notify_order_no,
      支付时间: r.pay_time,
      手机号: r.user_phone,
      设备ID: r.device_id ?? '',
      套餐: r.package_months_text,
      金额: r.amount,
      房源: r.house_name,
      房间: r.room_fang_hao,
      表号: r.meter_no,
      开通状态: r.grant_status_text,
      到期月: r.paid_until_after_text,
      关联充值单号: r.source_order_no,
    }));
    if (exportToCSV(rows, '语音购买记录')) {
      message.success(`已导出当前页 ${rows.length} 条`);
    }
  };

  const openRechargeDetail = async (sourceOrderId) => {
    if (!sourceOrderId) return;
    setRechargeModal({ open: true, loading: true, detail: null });
    try {
      const res = await getOrderDetail(sourceOrderId);
      if (res?.success && res.data) {
        setRechargeModal({ open: true, loading: false, detail: res.data });
      } else {
        message.error(res?.error || '查询充值单失败');
        setRechargeModal({ open: false, loading: false, detail: null });
      }
    } catch (err) {
      message.error(err.response?.data?.error || err.message || '查询充值单失败');
      setRechargeModal({ open: false, loading: false, detail: null });
    }
  };

  const columns = [
    { title: '通知单号', dataIndex: 'notify_order_no', key: 'notify_order_no', width: 180, ellipsis: true },
    { title: '支付时间', dataIndex: 'pay_time', key: 'pay_time', width: 170 },
    { title: '手机号', dataIndex: 'user_phone', key: 'user_phone', width: 120 },
    { title: '设备ID', dataIndex: 'device_id', key: 'device_id', width: 90, render: (v) => (v != null && v !== '' ? v : '-') },
    { title: '套餐', dataIndex: 'package_months_text', key: 'package_months_text', width: 80 },
    {
      title: '金额(元)',
      dataIndex: 'amount',
      key: 'amount',
      width: 90,
      render: (v) => (v != null ? Number(v).toFixed(2) : '-'),
    },
    { title: '房源', dataIndex: 'house_name', key: 'house_name', width: 140, ellipsis: true },
    { title: '房间', dataIndex: 'room_fang_hao', key: 'room_fang_hao', width: 100, ellipsis: true },
    { title: '表号', dataIndex: 'meter_no', key: 'meter_no', width: 120, ellipsis: true },
    {
      title: '开通',
      key: 'grant_status',
      width: 90,
      render: (_, r) => grantTag(r.grant_status_text, r.grant_status),
    },
    { title: '到期月', dataIndex: 'paid_until_after_text', key: 'paid_until_after_text', width: 90 },
    {
      title: '关联充值单',
      key: 'source_order',
      width: 200,
      render: (_, r) => (
        <Space size={4}>
          <span style={{ maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.source_order_no}>
            {r.source_order_no || r.source_order_id}
          </span>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => openRechargeDetail(r.source_order_id)}>
            详情
          </Button>
        </Space>
      ),
    },
  ];

  const d = rechargeModal.detail || {};

  return (
    <>
      <ListToolbar
        searchPlaceholder="单号/手机号/房源/房间/表号/充值单号"
        searchValue={searchText}
        onSearchChange={setSearchText}
        onSearch={handleSearch}
        onRefresh={() => fetchList(searchText.trim(), pagination.current, pagination.pageSize, payMonth, startDate, endDate)}
        onExport={handleExport}
        beforeFilters={(
          <Space size={6}>
            <Select
              size="small"
              style={{ width: 72 }}
              value={timeMode}
              onChange={handleTimeModeChange}
              options={TIME_MODE_OPTIONS}
            />
            {timeMode === 'day' ? (
              <>
                <Input
                  type="date"
                  size="small"
                  style={{ width: 138 }}
                  value={startDate}
                  onChange={handleStartDateChange}
                  title="开始日期"
                  allowClear
                />
                <span style={{ color: '#999', fontSize: 12 }}>至</span>
                <Input
                  type="date"
                  size="small"
                  style={{ width: 138 }}
                  value={endDate}
                  onChange={handleEndDateChange}
                  title="结束日期"
                  allowClear
                />
              </>
            ) : (
              <Select
                size="small"
                style={{ minWidth: 120 }}
                placeholder="选择月份"
                value={payMonth || undefined}
                onChange={handlePayMonthChange}
                options={PAY_MONTH_OPTIONS}
                allowClear
              />
            )}
          </Space>
        )}
      />
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col>
          <Statistic title="购买笔数" value={summary.count} suffix="笔" />
        </Col>
        <Col>
          <Statistic title="金额合计" value={summary.total_amount} precision={2} suffix="元" />
        </Col>
      </Row>
      <ResizableTable
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 1580 }}
      />
      <Modal
        title="关联充值订单"
        open={rechargeModal.open}
        footer={null}
        width={640}
        onCancel={() => setRechargeModal({ open: false, loading: false, detail: null })}
      >
        {rechargeModal.loading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>加载中…</div>
        ) : (
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="订单ID">{d.id ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="订单号">{d.order_no ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="支付状态">{d.order_status_text ?? d.order_status ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="支付时间">{d.pay_time || '-'}</Descriptions.Item>
            <Descriptions.Item label="订单金额">{d.order_amount != null ? Number(d.order_amount).toFixed(2) : '-'}</Descriptions.Item>
            <Descriptions.Item label="手机号">{d.user_phone ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="房源">{d.house_name ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="房间">{d.room_fang_hao ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="表号" span={2}>{d.current_meter_no || d.meter_no || '-'}</Descriptions.Item>
            <Descriptions.Item label="充值度数">{d.recharge_power ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="充值状态">{d.recharge_status_text ?? '-'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

const SubscriptionTab = () => {
  const [modal, modalContextHolder] = Modal.useModal();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [rechargeModal, setRechargeModal] = useState({ open: false, loading: false, detail: null });

  const fetchList = async (search, page, pageSize) => {
    setLoading(true);
    try {
      const res = await getAdminVoiceNotifySubscriptions(search, page, pageSize);
      if (res?.success && Array.isArray(res.data)) {
        setData(res.data);
        setPagination((prev) => ({
          ...prev,
          current: res.page ?? page,
          pageSize: res.page_size ?? pageSize,
          total: Number(res.total) || 0,
        }));
      } else {
        setData([]);
        message.error(res?.error || '查询失败');
      }
    } catch (err) {
      setData([]);
      message.error(err.response?.data?.error || err.message || '查询失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList('', 1, 20);
  }, []);

  const handleSearch = () => {
    fetchList(searchText.trim(), 1, pagination.pageSize);
  };

  const handleTableChange = (p) => {
    fetchList(searchText.trim(), p.current, p.pageSize);
  };

  const handleExport = () => {
    if (!data.length) {
      message.warning('当前页没有可导出数据');
      return;
    }
    const rows = data.map((r) => ({
      手机号: r.phone,
      设备ID: r.device_id,
      表号: r.meter_no,
      到期月: r.paid_until_text,
      状态: r.active_text,
      手动关闭: r.admin_disabled ? '是' : '否',
      关闭方: r.disabled_by_text || '',
      用户ID: r.user_id,
      最近充值单ID: r.last_source_order_id,
      更新时间: r.updated_at,
    }));
    if (exportToCSV(rows, '电话提醒当前订阅')) {
      message.success(`已导出当前页 ${rows.length} 条`);
    }
  };

  const openRechargeDetail = async (sourceOrderId) => {
    if (!sourceOrderId) return;
    setRechargeModal({ open: true, loading: true, detail: null });
    try {
      const res = await getOrderDetail(sourceOrderId);
      if (res?.success && res.data) {
        setRechargeModal({ open: true, loading: false, detail: res.data });
      } else {
        message.error(res?.error || '查询充值单失败');
        setRechargeModal({ open: false, loading: false, detail: null });
      }
    } catch (err) {
      message.error(err.response?.data?.error || err.message || '查询充值单失败');
      setRechargeModal({ open: false, loading: false, detail: null });
    }
  };

  const handleToggleAdminDisabled = (record, disabled) => {
    if (!record?.id) return;
    const doToggle = async () => {
      try {
        const res = await setAdminVoiceNotifySubscriptionAdminDisabled(record.id, disabled);
        if (res?.success) {
          message.success(res.msg || (disabled ? '已关闭' : '已恢复'));
          fetchList(searchText.trim(), pagination.current, pagination.pageSize);
        } else {
          message.error(res?.error || '操作失败');
        }
      } catch (err) {
        message.error(err.response?.data?.error || err.message || '操作失败');
      }
    };
    if (disabled) {
      modal.confirm({
        title: '关闭电话提醒',
        content: '关闭后即使订阅未到期也不再拨打电话，可随时恢复。确定关闭？',
        okText: '关闭',
        okType: 'danger',
        cancelText: '取消',
        zIndex: 3000,
        centered: true,
        onOk: doToggle,
      });
    } else {
      modal.confirm({
        title: '恢复电话提醒',
        content: '恢复后将按原到期日继续外呼。确定恢复？',
        okText: '恢复',
        cancelText: '取消',
        zIndex: 3000,
        centered: true,
        onOk: doToggle,
      });
    }
  };

  const columns = [
    { title: '手机号', dataIndex: 'phone', key: 'phone', width: 120 },
    { title: '设备ID', dataIndex: 'device_id', key: 'device_id', width: 90 },
    { title: '表号', dataIndex: 'meter_no', key: 'meter_no', width: 140, ellipsis: true },
    { title: '到期月', dataIndex: 'paid_until_text', key: 'paid_until_text', width: 90 },
    {
      title: '状态',
      key: 'active',
      width: 110,
      render: (_, r) => activeSubTag(r),
    },
    { title: '用户ID', dataIndex: 'user_id', key: 'user_id', width: 90 },
    {
      title: '最近充值单',
      key: 'last_source_order_id',
      width: 160,
      render: (_, r) => (
        r.last_source_order_id ? (
          <Button type="link" size="small" onClick={(e) => { e.stopPropagation(); openRechargeDetail(r.last_source_order_id); }}>
            {r.last_source_order_id}
          </Button>
        ) : '-'
      ),
    },
    { title: '更新时间', dataIndex: 'updated_at', key: 'updated_at', width: 170 },
    {
      title: '操作',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (_, r) => (
        r.admin_disabled ? (
          <Button type="link" size="small" onClick={(e) => { e.stopPropagation(); handleToggleAdminDisabled(r, false); }}>
            恢复
          </Button>
        ) : (
          <Button type="link" size="small" danger onClick={(e) => { e.stopPropagation(); handleToggleAdminDisabled(r, true); }}>
            关闭
          </Button>
        )
      ),
    },
  ];

  const d = rechargeModal.detail || {};

  return (
    <>
      {modalContextHolder}
      <ListToolbar
        searchPlaceholder="手机号/表号/设备ID/用户ID/充值单ID"
        searchValue={searchText}
        onSearchChange={setSearchText}
        onSearch={handleSearch}
        onRefresh={() => fetchList(searchText.trim(), pagination.current, pagination.pageSize)}
        onExport={handleExport}
      />
      <ResizableTable
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={data}
        pagination={pagination}
        onChange={handleTableChange}
        scroll={{ x: 1200 }}
      />
      <Modal
        title="关联充值订单"
        open={rechargeModal.open}
        footer={null}
        width={640}
        zIndex={3000}
        onCancel={() => setRechargeModal({ open: false, loading: false, detail: null })}
      >
        {rechargeModal.loading ? (
          <div style={{ padding: 24, textAlign: 'center' }}>加载中…</div>
        ) : (
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="订单ID">{d.id ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="订单号">{d.order_no ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="支付状态">{d.order_status_text ?? d.order_status ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="支付时间">{d.pay_time || '-'}</Descriptions.Item>
            <Descriptions.Item label="订单金额">{d.order_amount != null ? Number(d.order_amount).toFixed(2) : '-'}</Descriptions.Item>
            <Descriptions.Item label="手机号">{d.user_phone ?? '-'}</Descriptions.Item>
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

const VoiceNotifyOrderList = () => {
  const [activeTab, setActiveTab] = useState('orders');

  return (
    <div style={{ padding: '20px 0' }}>
      <PageTitle title="电话提醒管理" />
      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            { key: 'orders', label: '购买记录', children: <OrderTab /> },
            { key: 'subscriptions', label: '当前订阅', children: <SubscriptionTab /> },
          ]}
        />
      </Card>
    </div>
  );
};

export default VoiceNotifyOrderList;
